import React from 'react';
import { Company, PolicyType } from '../../types';
import { FileText, ShieldCheck, ArrowRight, ExternalLink } from 'lucide-react';

interface CompanyListProps {
  companies: Company[];
  onSelectCompany: (companyId: string, policyType: PolicyType) => void;
  onJobCreated?: (job: any) => void;
}

export const CompanyList: React.FC<CompanyListProps> = ({
  companies,
  onSelectCompany,
  onJobCreated,
}) => {
  const formatRelativeTime = (isoString?: string | null) => {
    if (!isoString) return 'Tracked';
    const date = new Date(isoString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };
  if (companies.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-12 text-center border border-slate-200 dark:border-slate-700">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          No companies found matching your search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 font-semibold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="py-3.5 px-4">Company</th>
              <th className="py-3.5 px-4">Category</th>
              <th className="py-3.5 px-4">Policies</th>
              <th className="py-3.5 px-4 text-center">Total Revisions</th>
              <th className="py-3.5 px-4">Last Updated</th>
              <th className="py-3.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {companies.map((c) => {
              return (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-white">
                    <div className="flex items-center gap-2.5">
                      <button
                        onClick={() => onSelectCompany(c.id, 'terms_of_service')}
                        className="text-sm font-bold hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer text-left"
                        title={`View ${c.name} policies`}
                      >
                        {c.name}
                      </button>
                      <a
                        href={`https://${c.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 inline-flex items-center gap-0.5"
                      >
                        <span className="font-normal font-mono text-[11px]">({c.domain})</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </td>
                  <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[11px]">
                      {c.category}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 space-x-2">
                    <button
                      onClick={() => onSelectCompany(c.id, 'terms_of_service')}
                      className="inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                    >
                      <FileText className="w-3 h-3" />
                      Terms ({c.revisionCounts?.tos || 2})
                    </button>
                    <button
                      onClick={() => onSelectCompany(c.id, 'privacy_policy')}
                      className="inline-flex items-center gap-1 text-[11px] text-teal-600 dark:text-teal-400 hover:underline cursor-pointer"
                    >
                      <ShieldCheck className="w-3 h-3" />
                      Privacy ({c.revisionCounts?.privacy || 2})
                    </button>
                  </td>
                  <td className="py-3.5 px-4 text-center font-bold text-slate-800 dark:text-slate-200 font-mono">
                    {c.totalRevisions || c.total_revisions || c.revisionCounts?.total || 4}
                  </td>
                  <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">
                    {formatRelativeTime(c.updatedAt || c.updated_at || c.lastScrapedAt || c.last_scraped_at)}
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      onClick={() => onSelectCompany(c.id, 'terms_of_service')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                    >
                      Explore
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
